import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { AstraParagraph, getOnlyDate, Spinner } from '~/astra'
import { useStore } from '~/store/store'
import { useActiveRecurringAssignments } from '~/storm'

import { Practitioners } from '../../../TablesLayout/Practitioners/Practitioners'
import { RecurringAssignmentToggleList } from '../RecurringAssignmentToggleList'
import { useApplyRecurringAssignmentsQuery } from './useApplyRecurringAssignmentsQuery'
import { useApplyPreviewData } from './utils'

const Loader = () => (
    <div className="flex flex-col items-center gap-8">
        <Spinner size="lg" />
        <AstraParagraph variant="body" weight="medium">
            Laster forhåndsvisning...
        </AstraParagraph>
    </div>
)

type Props = {
    onClose: () => void
}

export const ApplyPreviewDialog = ({ onClose }: Props) => {
    const { setRecurringAssignmentApplyPreviewData, setShowRecurringAssignmentApplyPreview } = useStore(
        useShallow(state => ({
            setRecurringAssignmentApplyPreviewData: state.surgeonScheduler.actions.setRecurringAssignmentApplyPreviewData,
            setShowRecurringAssignmentApplyPreview: state.surgeonScheduler.actions.setShowRecurringAssignmentApplyPreview,
        }))
    )
    const selectedDate = useStore(state => state.appFilters.selectedDate)
    const firstDayOfMonth = selectedDate.startOf('month')
    const lastDayOfMonth = selectedDate.endOf('month')

    const recurringAssignments = useActiveRecurringAssignments()
    const deselectedRecurringAssignmentUuids = useStore(state => state.surgeonScheduler.deselectedRecurringAssignmentUuids)
    const allRecurringAssignmentUuids = recurringAssignments.map(recurring => recurring.uuid)
    const selectedRecurringAssignmentUuids = allRecurringAssignmentUuids.filter(uuid => !deselectedRecurringAssignmentUuids.includes(uuid))

    const [isApplying, setIsApplying] = useState(false)

    const { data, isLoading, isFetching } = useApplyRecurringAssignmentsQuery({
        recurring_assignment_uuids: selectedRecurringAssignmentUuids,
        start_date: getOnlyDate(firstDayOfMonth),
        end_date: getOnlyDate(lastDayOfMonth),
        preview: !isApplying,
    })

    const previewData = useApplyPreviewData(data)

    useEffect(() => {
        setRecurringAssignmentApplyPreviewData(previewData)
        setShowRecurringAssignmentApplyPreview(true)
    }, [previewData, setRecurringAssignmentApplyPreviewData, setShowRecurringAssignmentApplyPreview])

    useEffect(() => {
        if (!isApplying) return
        if (isFetching) return
        onClose()
    }, [isApplying, isFetching, onClose])

    return (
        <div className="flex h-[85vh] w-[95vw] items-start justify-center p-8">
            <div className="flex h-full w-full justify-end gap-4">
                {isLoading ? (
                    <div className="flex h-full w-full items-center justify-center">
                        <Loader />
                    </div>
                ) : (
                    <div className="h-full w-full overflow-auto">
                        <Practitioners isLoading={isLoading} isPreview />
                    </div>
                )}
                <RecurringAssignmentToggleList onApply={() => setIsApplying(true)} disabledApplyButton={isApplying} />
            </div>
        </div>
    )
}
